import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FaSyncAlt,
  FaArrowLeft,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaExclamationCircle,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const toISTDate = (ts) => ts
  ? new Date(new Date(ts).getTime() + IST_OFFSET_MS).toISOString().split("T")[0]
  : null;

export default function CommissionCheck() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const todayStr = new Date(Date.now() + IST_OFFSET_MS).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const filtered = (data.transactions || []).filter((tx) => {
        const isValidStatus = ["APPROVED", "DELIVERED"].includes(tx.status);
        const hasAmount = Number(tx.amount) > 0;
        const type = tx.paymentType?.toUpperCase().trim();
        const isValidType = ["IN", "OUT", "SPLITIN", "SPLITOUT"].includes(type);
        return isValidStatus && hasAmount && isValidType;
      });
      setTransactions(filtered);
    } catch (err) {
      console.error("[FETCH ERROR]", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- CORE AUDIT LOGIC ---
  const ledgerData = useMemo(() => {
    const rows = [];

    // Group all transactions by Base ID (including TP)
    const groups = transactions.reduce((acc, tx) => {
      const parts = tx.paymentId.split("-");
      const baseId = parts[0] + "-" + parts[1];
      if (!acc[baseId]) acc[baseId] = [];
      acc[baseId].push(tx);
      return acc;
    }, {});

    Object.keys(groups).forEach((baseId) => {
      const cluster = groups[baseId];
      const isTPCluster = cluster.some((t) => t.paymentId.endsWith("-TP"));

      let origin, recipients, inAmt, inComm, totalOutComm, txDate;

      if (isTPCluster) {
        const tpEntry = cluster.find((t) => t.paymentId.endsWith("-TP"));
        const lakEntry = cluster.find((t) => t.paymentId.endsWith("-LAK"));
        if (!tpEntry) return;
        txDate = toISTDate(tpEntry.timestamp);
        if (startDate && txDate < startDate) return;
        if (endDate && txDate > endDate) return;
        origin = tpEntry;
        recipients = lakEntry ? [lakEntry] : [];
        inAmt = Number(tpEntry.amount || 0);
        inComm = Number(tpEntry.commission || 0);
        totalOutComm = Number(lakEntry?.commission || 0);
      } else {
        origin = cluster.find(
          (t) => t.flowRole === "SENDER" && !t.paymentId.includes("PART"),
        );
        if (!origin) return;
        txDate = toISTDate(origin.timestamp);
        if (startDate && txDate < startDate) return;
        if (endDate && txDate > endDate) return;
        recipients = cluster.filter(
          (t) => t.flowRole === "RECEIVER" || t.paymentId.includes("PART"),
        );
        inAmt = Number(origin.amount || 0);
        inComm = Number(origin.commission || 0);
        totalOutComm = recipients.reduce(
          (sum, r) => sum + Number(r.commission || 0), 0,
        );
      }

      // --- RULE: ONE SIDE ONLY ---
      const senderHas = inComm > 0;
      const receiverHas = totalOutComm > 0;
      const isOneSide =
        (senderHas || receiverHas) && !(senderHas && receiverHas);

      if (!isOneSide) return;

      // --- FORMULA CALCULATION ---
      const actualTotalPaid = inComm + totalOutComm;
      const inRate =
        origin.fromBranch === "BLR" || origin.toBranch === "BLR" ? 300 : 250;
      const expectedComm = (inAmt / 100000) * inRate;

      // Positive = Less Commission (Owed)
      // Negative = Additional Commission (Overpaid)
      const diff = expectedComm - actualTotalPaid;

      // Skip if it matches the formula perfectly
      if (Math.abs(diff) < 0.1) return;

      rows.push({
        sNo: rows.length + 1,
        branch: origin.fromBranch,
        amount: inAmt,
        comm: inComm,
        outComm: totalOutComm,
        target:
          isTPCluster
            ? `${origin.thirdParty} (TP)`
            : recipients.length > 1
              ? recipients.map((r) => r.toBranch).join(", ")
              : recipients[0]?.toBranch || "N/A",
        isTP: isTPCluster,
        actualValue: expectedComm,
        diff: diff,
        lessComm: diff > 0 ? diff : 0,
        additionalComm: diff < 0 ? Math.abs(diff) : 0,
      });
    });

    return rows;
  }, [transactions, startDate, endDate,]);

  // --- FOOTER TOTALS LOGIC ---
  const totals = useMemo(() => {
    return ledgerData.reduce(
      (acc, row) => {
        acc.sumLess += Number(row.lessComm || 0);
        acc.sumAdd += Number(row.additionalComm || 0);
        acc.netAudit = acc.sumLess - acc.sumAdd;
        return acc;
      },
      { netAudit: 0, sumLess: 0, sumAdd: 0 },
    );
  }, [ledgerData, ]);

  return (
    <div
      className="admin-container"
      style={{ padding: "20px", background: "#f8fafc", minHeight: "100vh" }}
    >
      {/* Header Section */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          background: "#fff",
          padding: "15px 20px",
          borderRadius: "10px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "1.4rem",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#1e293b",
          }}
        >
          <FaFileInvoiceDollar color="#3b82f6" /> COMMISSION VERIFICATION LEDGER
        </h1>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={fetchData} className="br-btn" disabled={loading}>
            <FaSyncAlt className={loading ? "spin" : ""} /> Refresh
          </button>
          <Link to="/commission-audit">
            <button
              className="Branch-nav"
             
            >
              <FaArrowLeft /> Back to Admin
            </button>
          </Link>
        </div>
      </header>

      {/* Filter Bar */}
      <div
        style={{
          background: "#fff",
          padding: "15px",
          borderRadius: "10px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          marginBottom: "25px",
          display: "flex",
          gap: "20px",
          alignItems: "center",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#ef4444",
            fontWeight: "bold",
            fontSize: "0.9rem",
          }}
        >
          <FaExclamationCircle /> <span>Mistakes: One-Side Entry Only</span>
        </div>
        <div style={{ borderLeft: "1px solid #cbd5e1", height: "20px" }}></div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FaCalendarAlt color="#64748b" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              border: "1px solid #cbd5e1",
              padding: "5px",
              borderRadius: "4px",
            }}
          />
          <span style={{ color: "#64748b" }}>to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              border: "1px solid #cbd5e1",
              padding: "5px",
              borderRadius: "4px",
            }}
          />
        </div>
        {/* <button
          onClick={() => {
            setStartDate("");
            setEndDate("");
          }}
          className="br-btn-small"
          style={{ background: "#f1f5f9", color: "#475569" }}
        >
          View All History
        </button> */}
      </div>

      {/* Ledger Table */}
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          overflowX: "auto",
        }}
      >
        <table
          className="branch-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.85rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              <th
                colSpan="4"
                style={{ padding: "12px", borderBottom: "1px solid #cbd5e1" }}
              >
                IN-BRANCH (SENDER)
              </th>
              <th colSpan="3" style={{ borderBottom: "1px solid #cbd5e1" }}>
                OUT-BRANCH (RECEIVERS)
              </th>
              <th
                style={{
                  background: "#fef08a",
                  borderBottom: "1px solid #cbd5e1",
                }}
              >
                FORMULA
              </th>
              <th
                colSpan="3"
                style={{
                  background: "#fee2e2",
                  borderBottom: "1px solid #cbd5e1",
                }}
              >
                AUDIT RESULT
              </th>
            </tr>
            <tr
              style={{
                background: "#f1f5f9",
                borderBottom: "2px solid #cbd5e1",
              }}
            >
              <th>S.NO</th>
              <th>BRANCH</th>
              <th>AMT</th>
              <th>COM</th>
              <th>TARGETS</th>
              <th>AMT</th>
              <th>COM</th>
              <th style={{ background: "#fef08a" }}>ACTUAL VAL</th>
              <th style={{ background: "#fee2e2" }}>DIFF</th>
              <th style={{ background: "#fee2e2" }}>LESS</th>
              <th style={{ background: "#dcfce7" }}>ADDITIONAL</th>
            </tr>
          </thead>
          <tbody>
            {ledgerData.length > 0 ? (
              ledgerData.map((row, idx) => (
                <tr
                  key={idx}
                  style={{
                    textAlign: "center",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <td>{row.sNo}</td>
                  <td style={{ fontWeight: "bold" }}>
                    {row.branch}
                    {row.isTP && <span style={{ marginLeft: 4, fontSize: "0.65rem", background: "#7c3aed", color: "#fff", borderRadius: 3, padding: "1px 4px", fontWeight: 700 }}>TP</span>}
                  </td>
                  <td>{row.amount.toLocaleString()}</td>
                  <td style={{ fontWeight: row.comm > 0 ? "bold" : "normal" }}>
                    {row.comm}
                  </td>
                  <td style={{ fontSize: "0.75rem", color: "#64748b" }}>
                    {row.target}
                  </td>
                  <td>{row.amount.toLocaleString()}</td>
                  <td
                    style={{
                      background: row.outComm > 0 ? "#dcfce7" : "transparent",
                      fontWeight: "bold",
                    }}
                  >
                    {row.outComm}
                  </td>
                  <td style={{ background: "#fffbeb" }}>{row.actualValue}</td>
                  <td
                    style={{
                      color: row.diff >= 0 ? "#ef4444" : "#22c55e",
                      fontWeight: "bold",
                    }}
                  >
                    {row.diff}
                  </td>
                  <td style={{ color: "#ef4444" }}>{row.lessComm || ""}</td>
                  <td style={{ color: "#22c55e", fontWeight: "bold" }}>
                    {row.additionalComm || ""}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="11"
                  style={{
                    textAlign: "center",
                    padding: "50px",
                    color: "#94a3b8",
                  }}
                >
                  All transactions are balanced. No "One-Side" commission
                  mistakes found.
                </td>
              </tr>
            )}

            {ledgerData.length > 0 && (
              <tr
                style={{
                  background: "#f8fafc",
                  fontWeight: "bold",
                  borderTop: "2px solid #1e293b",
                }}
              >
                <td
                  colSpan="8"
                  style={{ textAlign: "right", paddingRight: "20px" }}
                >
                  TOTAL MISMATCH
                </td>
                <td
                  style={{
                    color: totals.netAudit >= 0 ? "#ef4444" : "#22c55e",
                  }}
                >
                  {totals.netAudit}
                </td>
                <td style={{ color: "#ef4444" }}>{totals.sumLess}</td>
                <td style={{ color: "#22c55e" }}>{totals.sumAdd}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
